/**
 * Coffee By Region — synced dual carousel (region homepage variant).
 *
 * The right-hand product carousel and its hidden text tabs are the
 * theme's existing `carousel-with-tabs` component (assets/base.js) —
 * DO NOT modify that file. This script only:
 *   1. Wires the left panel's prev/next arrows to step through the
 *      hidden `[carousel-tab]` buttons (clicking them is what makes
 *      base.js swap the product slides).
 *   2. Keeps the left region panel (image + name) in sync with
 *      whichever tab is currently active, by watching for the
 *      `active` class base.js toggles on the tabs.
 */
function initRegionCollectionCarousels() {
  const sections = document.querySelectorAll('.featured-collection--region');

  sections.forEach((section) => {
    if (section.dataset.regionCarouselInit === 'true') return;

    const panelList = section.querySelector('[data-region-panel-list]');
    const tabsWrapper = section.querySelector('.swiper-tabs-wrapper');
    if (!panelList || !tabsWrapper) return;

    const panels = Array.from(panelList.querySelectorAll('[data-region-panel]'));
    const getTabs = () => Array.from(section.querySelectorAll('[carousel-tab]'));

    // The right-hand product carousel is arrow-driven only — disable Swiper's
    // touch/drag so it can't be free-scrolled (the prev/next arrows still work).
    const getProductSwiper = () => {
      const candidates = [
        section.querySelector('[main-slider]'),
        section.querySelector('carousel-with-tabs'),
        section.querySelector('.swiper-container'),
        section.querySelector('.swiper'),
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        if (candidates[i] && candidates[i].swiper) return candidates[i].swiper;
      }
      return null;
    };
    const disableProductDrag = () => {
      const swiper = getProductSwiper();
      if (!swiper) return false;
      swiper.allowTouchMove = false;
      if (swiper.params) swiper.params.allowTouchMove = false;
      return true;
    };

    const syncPanels = () => {
      const activeTab = getTabs().find((tab) => tab.classList.contains('active'));
      if (!activeTab) return;
      const activeId = activeTab.getAttribute('data-tab');
      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.getAttribute('data-region-panel') === activeId);
      });
      // Re-apply on each switch in case base.js re-inits the Swiper.
      disableProductDrag();
    };

    const stepToTab = (direction) => {
      const tabs = getTabs();
      if (!tabs.length) return;
      const currentIndex = tabs.findIndex((tab) => tab.classList.contains('active'));
      if (currentIndex === -1) return;
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
      if (nextIndex === currentIndex) return;
      tabs[nextIndex].click();
    };

    panelList.querySelectorAll('[data-region-prev]').forEach((button) => {
      button.addEventListener('click', () => stepToTab(-1));
    });
    panelList.querySelectorAll('[data-region-next]').forEach((button) => {
      button.addEventListener('click', () => stepToTab(1));
    });

    // base.js's CarouselWithTabs#switchTabs toggles the `active` class on
    // [carousel-tab] buttons (inside a 100ms setTimeout) whenever a tab is
    // clicked — including the clicks this script triggers above. Observe
    // that to keep the left panel's active slide in sync.
    const observer = new MutationObserver(syncPanels);
    observer.observe(tabsWrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    syncPanels();

    // Swiper may initialize slightly after this script runs — retry disabling
    // touch/drag until the instance exists (or we give up after a few tries).
    let dragTries = 0;
    const ensureDragDisabled = () => {
      if (disableProductDrag() || dragTries > 20) return;
      dragTries += 1;
      setTimeout(ensureDragDisabled, 150);
    };
    ensureDragDisabled();

    section.dataset.regionCarouselInit = 'true';
  });
}

document.addEventListener('DOMContentLoaded', initRegionCollectionCarousels);

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initRegionCollectionCarousels);
  document.addEventListener('shopify:section:select', initRegionCollectionCarousels);
}
